/**
 * Minimal fetch wrapper for the backend REST API.
 * VITE_API_BASE_URL is the single source of the full API prefix (origin + version,
 * e.g. /rest/v1 or https://api.example.com/rest/v2) — a version bump is an env-var
 * change only, never a code change. Falls back to /rest/v1 for local dev (matches
 * the Vite proxy in vite.config.ts); production sets it explicitly in .env.production.
 */

import { endsBelievedSession } from './sessionEnd';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/rest/v1';

/**
 * listing_photos.object_key → fetchable URL. Streamed through the backend
 * (GET /rest/v1/photos/:key) rather than a direct MinIO URL — MinIO isn't
 * publicly exposed, so this works in every
 * environment today via the same origin/proxy VITE_API_BASE_URL already uses,
 * no new infra dependency.
 */
export function assetUrl(key: string): string {
  return `${API_BASE}/photos/${key}`;
}

/** status 0 = network failure (no response). */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** Backend's machine-readable 401 cause, e.g. 'token_expired', 'account_banned'. */
    public readonly reason?: string,
    /** Only present on an account_banned 401 whose ban recorded a reason. */
    public readonly banReason?: string,
    /** Field errors from a zod 400, e.g. ["title: Title too long"]. */
    public readonly details?: string[]
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The backend revokes a session mid-flight when an admin bans or deactivates
 * the account: standing is checked on every request, so the very next call
 * 401s and the auth cookie is cleared.
 *
 * WHY THIS LIVES IN THE API CLIENT
 * Nothing else sees every response. `getCurrentUser` is probed once at boot,
 * so without this a banned user's open tab keeps rendering the logged-in UI
 * and every action fails with a generic error until they happen to reload.
 * One notifier here covers both Vite entries — the app and the panel share
 * this module.
 */
/**
 * `account_deactivated` is deliberately gone. Passive no longer ends a session
 * — the user set it themselves and has to log in to undo it — so treating it
 * as a revocation would pop "your session was ended" at their own login.
 */
export type SessionRevokedReason = 'account_banned' | 'account_missing' | 'token_expired';

export interface SessionRevocation {
  reason: SessionRevokedReason;
  banReason?: string;
}

/**
 * An admin ending someone's session. These carry their own copy and always
 * announce, because they are not something the user can undo by signing in.
 *
 * An ordinary expiry is NOT here: it cannot be recognised by reason code alone.
 * See `sessionEnd.ts` — the same `missing_token` means "you are a guest" or
 * "your session just ended" depending on whether a user was cached.
 */
const REVOKED_REASONS: ReadonlySet<string> = new Set<SessionRevokedReason>([
  'account_banned',
  'account_missing',
]);

/**
 * Whether the app is currently rendering as signed in. Written by the auth
 * provider; read here because nothing else sees every response. Without it a
 * guarded call from a signed-out visitor is indistinguishable from a lapsed
 * session, and one of the two gets the wrong message.
 */
let sessionBelievedActive = false;

export function setSessionBelievedActive(active: boolean): void {
  sessionBelievedActive = active;
}

type RevocationListener = (revocation: SessionRevocation) => void;

const revocationListeners = new Set<RevocationListener>();

/** Subscribe to mid-session revocation. Returns an unsubscribe function. */
export function onSessionRevoked(listener: RevocationListener): () => void {
  revocationListeners.add(listener);
  return () => revocationListeners.delete(listener);
}

function notifyRevoked(revocation: SessionRevocation): void {
  // A listener throwing must not turn an already-failed request into a
  // different, more confusing failure — the 401 still needs to reach its caller.
  for (const listener of revocationListeners) {
    try {
      listener(revocation);
    } catch {
      /* a broken subscriber is not the failing request's problem */
    }
  }
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /**
   * Suppress the session-ended notice for this call. For probes that ask
   * "is anyone signed in?" and treat "no" as an answer rather than a failure;
   * without it, opening the app after a week announces that a session ended.
   */
  silentRevocation?: boolean;
}

async function handleResponse<T>(res: Response, silentRevocation = false): Promise<T> {
  if (!res.ok) {
    // Backend error bodies: { message } (globalErrorHandler) or { error, details } (zod 400s).
    // A 401 additionally carries { reason } and, for a ban, { banReason }.
    let message = '';
    let reason: string | undefined;
    let banReason: string | undefined;
    let details: string[] | undefined;
    try {
      const data = (await res.json()) as {
        message?: string;
        error?: string;
        reason?: string;
        banReason?: string;
        details?: string[];
      };
      message = data.message ?? data.error ?? '';
      reason = data.reason;
      banReason = data.banReason;
      details = Array.isArray(data.details) ? data.details : undefined;
    } catch {
      /* non-JSON error body — status alone drives the UI message */
    }

    if (res.status === 401 && !silentRevocation) {
      if (reason && REVOKED_REASONS.has(reason)) {
        notifyRevoked({ reason: reason as SessionRevokedReason, banReason });
      } else if (endsBelievedSession(reason, sessionBelievedActive)) {
        // Reported as one thing to the UI whatever the code was: from the
        // user's side "expired", "gone" and "rejected" are the same event.
        notifyRevoked({ reason: 'token_expired' });
      }
    }

    throw new ApiError(res.status, message, reason, banReason, details);
  }
  // 204 has no body, so res.json() throws a parse error the caller reads as a
  // failed request — DELETE /users/me succeeded server-side while the app
  // reported "silinemedi" and kept the session up until the next reload.
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return (await res.json()) as T;
}

export async function api<T>(
  path: string,
  { method = 'GET', body, silentRevocation = false }: ApiOptions = {}
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: 'include', // auth is an httpOnly cookie
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'network');
  }
  return handleResponse<T>(res, silentRevocation);
}

/** Multipart POST — the browser sets Content-Type + boundary automatically for FormData. */
export async function apiUpload<T>(path: string, formData: FormData): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
  } catch {
    throw new ApiError(0, 'network');
  }
  return handleResponse<T>(res);
}
